import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, UserRound, LogOut, LayoutDashboard, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/blogs", label: "Blogs" },
];

const ACCOUNT_LINKS = [
  { to: "/account/book", label: "Book", testid: "book" },
  { to: "/account", label: "My Pups", testid: "pups" },
  { to: "/account/bookings", label: "My Bookings", testid: "bookings" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setOpen(false);
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const accountTo = user?.role === "admin" ? "/admin" : "/account";
  const accountLabel = user?.role === "admin" ? "Admin Panel" : "My Account";
  const isCustomer = Boolean(user) && user.role !== "admin";
  const initials = (user?.name || "?")
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const avatarEl = (
    <span className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-navy text-xs font-bold text-cream">
      {user?.picture ? (
        <img src={user.picture} alt="" className="h-full w-full object-cover" />
      ) : initials ? (
        initials
      ) : (
        <UserRound className="h-4 w-4" aria-hidden="true" />
      )}
    </span>
  );

  const goSection = () => setOpen(false);


  return (
    <header
      data-testid="nav-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow] duration-300 ${
        scrolled || open
          ? "bg-cream/85 shadow-[0_1px_0_rgba(15,23,42,0.07)] backdrop-blur-xl"
          : "bg-transparent"
      }`}
    >
      <div className="relative mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <Link to="/" data-testid="nav-logo" aria-label="WhatTheBark home" className="flex items-center">
          <img
            src="/logo.png"
            alt="WhatTheBark logo"
            className="h-12 w-auto mix-blend-multiply sm:h-14"
          />
        </Link>

        <nav className="hidden items-center gap-1 md:flex" aria-label="Primary">
          {!isCustomer &&
            LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                    isActive ? "bg-navy text-cream" : "text-navy/70 hover:text-navy"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          {isCustomer &&
            ACCOUNT_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/account"}
                data-testid={`nav-link-${l.testid}`}
                className={({ isActive }) =>
                  `rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                    isActive ? "bg-navy text-cream" : "text-navy/70 hover:text-navy"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          {isCustomer && (
            <NavLink
              to="/account/rates"
              data-testid="nav-link-rates"
              className={({ isActive }) =>
                `rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                  isActive ? "bg-navy text-cream" : "text-navy/70 hover:text-navy"
                }`
              }
            >
              Rates
            </NavLink>
          )}
          {isCustomer ? (
            <button
              type="button"
              data-testid="nav-avatar-button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Account menu"
              className="ml-3 flex items-center gap-2 rounded-full border border-navy/15 py-1 pl-1 pr-3 transition-colors duration-200 hover:border-bark"
            >
              {avatarEl}
              <ChevronDown className="h-4 w-4 text-navy/60" aria-hidden="true" />
            </button>
          ) : user ? (
            <div className="ml-3 flex items-center gap-2">
              <Link
                to={accountTo}
                data-testid="nav-account-button"
                className="inline-flex items-center gap-2 rounded-full bg-bark px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-bark-dark"
              >
                <LayoutDashboard className="h-4 w-4" aria-hidden="true" />
                {accountLabel}
              </Link>
              <button
                type="button"
                onClick={handleLogout}
                data-testid="nav-logout-button"
                aria-label="Log out"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-navy/15 text-navy transition-colors duration-200 hover:border-bark hover:text-bark"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <Link
              to="/signin"
              data-testid="nav-signin-button"
              className="ml-3 inline-flex items-center gap-2 rounded-full bg-bark px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-bark-dark"
            >
              <UserRound className="h-4 w-4" aria-hidden="true" />
              Customer Login
            </Link>
          )}
        </nav>

        {isCustomer ? (
          <button
            type="button"
            data-testid="nav-avatar-button-mobile"
            aria-label="Account menu"
            onClick={() => setMenuOpen((v) => !v)}
            className="inline-flex h-11 w-11 items-center justify-center overflow-hidden rounded-full border border-navy/15 md:hidden"
          >
            {avatarEl}
          </button>
        ) : (
          <button
            type="button"
            data-testid="nav-mobile-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-navy text-cream md:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        )}

        {isCustomer && menuOpen && (
          <div
            data-testid="account-menu"
            className="absolute right-4 top-full mt-2 w-60 rounded-2xl border border-ink/10 bg-cream/95 p-2 shadow-[0_24px_60px_-28px_rgba(15,23,42,0.5)] backdrop-blur-xl sm:right-6 lg:right-8"
          >
            <div className="border-b border-ink/5 px-3 py-2">
              <p className="truncate text-sm font-semibold text-ink">{user.name}</p>
              <p className="truncate text-xs text-slate-500">{user.email}</p>
            </div>
            <Link to="/account" data-testid="menu-account" onClick={() => setMenuOpen(false)} className="mt-1 block rounded-xl px-3 py-2.5 text-sm font-semibold text-navy transition-colors duration-200 hover:bg-cream-dark">
              My account
            </Link>
            <Link to="/account/profile" data-testid="menu-profile" onClick={() => setMenuOpen(false)} className="block rounded-xl px-3 py-2.5 text-sm font-semibold text-navy transition-colors duration-200 hover:bg-cream-dark">
              My profile
            </Link>
            <Link to="/account/rates" data-testid="menu-rates" onClick={() => setMenuOpen(false)} className="block rounded-xl px-3 py-2.5 text-sm font-semibold text-navy transition-colors duration-200 hover:bg-cream-dark">
              Rates
            </Link>
            <button
              type="button"
              data-testid="menu-logout"
              onClick={() => { setMenuOpen(false); handleLogout(); }}
              className="block w-full rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-red-500 transition-colors duration-200 hover:bg-red-50"
            >
              Log out
            </button>
          </div>
        )}
      </div>

      {open && (
        <nav
          data-testid="nav-mobile-menu"
          className="border-t border-ink/5 bg-cream/95 px-4 pb-6 pt-2 backdrop-blur-xl md:hidden"
          aria-label="Mobile"
        >
          {!isCustomer &&
            LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `block rounded-xl px-4 py-3 text-base font-semibold ${
                    isActive ? "bg-navy text-cream" : "text-navy hover:bg-cream-dark"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          {isCustomer &&
            ACCOUNT_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/account"}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.testid}`}
                className={({ isActive }) =>
                  `block rounded-xl px-4 py-3 text-base font-semibold ${
                    isActive ? "bg-navy text-cream" : "text-navy hover:bg-cream-dark"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          {isCustomer && (
            <NavLink
              to="/account/rates"
              data-testid="nav-mobile-link-rates"
              className={({ isActive }) =>
                `block rounded-xl px-4 py-3 text-base font-semibold ${
                  isActive ? "bg-navy text-cream" : "text-navy hover:bg-cream-dark"
                }`
              }
            >
              Rates
            </NavLink>
          )}
          {user ? (
            <>
              <Link
                to={accountTo}
                data-testid="nav-mobile-account-button"
                className="mt-3 flex items-center justify-center gap-2 rounded-full bg-bark px-5 py-3.5 text-base font-semibold text-white"
              >
                <LayoutDashboard className="h-5 w-5" aria-hidden="true" />
                {accountLabel}
              </Link>
              <button
                type="button"
                onClick={handleLogout}
                data-testid="nav-mobile-logout-button"
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-full border border-navy/15 px-5 py-3 text-base font-semibold text-navy"
              >
                <LogOut className="h-5 w-5" aria-hidden="true" />
                Log out
              </button>
            </>
          ) : (
            <Link
              to="/signin"
              data-testid="nav-mobile-signin-button"
              className="mt-3 flex items-center justify-center gap-2 rounded-full bg-bark px-5 py-3.5 text-base font-semibold text-white"
            >
              <UserRound className="h-5 w-5" aria-hidden="true" />
              Customer Login
            </Link>
          )}
        </nav>
      )}
    </header>
  );
};
